import { API_BASE } from '../config/api';
import AuthService from './authService';
import SavedItemsServiceDB from './savedItemsServiceDB';

class SavedItemsService {
  static getCurrentUser() {
    return AuthService.getCurrentUser() || { id: 'guest', name: 'Guest' };
  }

  // SAVE SYSTEM - Personal reference for later use
  
  // Save a trip for personal reference
  static async saveTrip(trip, tags = []) {
    return await SavedItemsServiceDB.saveTrip(trip, tags);
  }

  // Save an activity for personal reference
  static async saveActivity(activity, collectionId = null, { foundVia = null } = {}) {
    return await SavedItemsServiceDB.saveActivity(activity, null, [], collectionId, { foundVia });
  }

  // Remove saved trip
  static async unsaveTrip(tripId) {
    return await SavedItemsServiceDB.unsaveTrip(tripId);
  }

  // Remove saved activity
  static async unsaveActivity(activityId) {
    return await SavedItemsServiceDB.unsaveActivity(activityId);
  }

  // Saved activities are not archivable — use unsaveActivity. archiveActivity,
  // unarchiveActivity and getArchivedActivities lived here; their endpoints are
  // gone. getAllArchivedItems (collections + boards) is the archive read path.

  // Get all archived items (activities, collections, boards, albums)
  static async getAllArchivedItems() {
    return await SavedItemsServiceDB.getAllArchivedItems();
  }

  // Note: Collection management (add/remove/move) is handled via CollectionsService

  // Check if trip is saved by user (DATABASE VERSION)
  static async isTripSaved(tripId, userId = null) {
    return await SavedItemsServiceDB.isTripSaved(tripId);
  }

  // Check if activity is saved by user (DATABASE VERSION)
  static async isActivitySaved(activityId, userId = null) {
    return await SavedItemsServiceDB.isActivitySaved(activityId);
  }

  // Get a specific saved activity by ID (DATABASE VERSION)
  static async getSavedActivity(activityId, userId = null) {
    return await SavedItemsServiceDB.getSavedActivity(activityId);
  }

  // Get saved trips with filtering/sorting (DATABASE VERSION)
  static async getSavedTrips(userId = null, filters = {}) {
    return await SavedItemsServiceDB.getSavedTrips(userId, filters);
  }

  // Get saved activities with filtering/sorting (DATABASE VERSION)
  // Supports pagination with { limit, offset } in filters
  // Returns { activities, pagination } when pagination params provided, otherwise just array
  static async getSavedActivities(userId = null, filters = {}) {
    return await SavedItemsServiceDB.getSavedActivities(userId, filters);
  }

  // Get all saved activity IDs for efficient bulk lookups (e.g., search results saved status)
  static async getSavedActivityIds() {
    return await SavedItemsServiceDB.getSavedActivityIds();
  }


  // UTILITY METHODS

  // Get all unique tags from saved items (DATABASE VERSION)
  static async getAllTags(userId = null) {
    try {
      const activities = await SavedItemsServiceDB.getSavedActivities(userId, {});
      const trips = await SavedItemsServiceDB.getSavedTrips(userId, {});
      const allTags = new Set();

      // Extract tags from activities
      activities.forEach(activity => {
        if (activity.tags && Array.isArray(activity.tags)) {
          activity.tags.forEach(tag => allTags.add(tag));
        }
      });

      // Extract tags from trips
      trips.forEach(trip => {
        if (trip.tags && Array.isArray(trip.tags)) {
          trip.tags.forEach(tag => allTags.add(tag));
        }
      });

      return Array.from(allTags).sort();
    } catch (error) {
      console.error('Error getting all tags:', error);
      return [];
    }
  }


  // Load more photos for a saved activity/place
  static async loadMorePhotos(placeId, currentOffset, limit = 5) {
    try {
      const response = await fetch(
        `${API_BASE}/api/saves/places/${placeId}/photos?offset=${currentOffset}&limit=${limit}`,
        {
          headers: {
            ...AuthService.getAuthHeaders()
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load more photos');
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading more photos:', error);
      throw error;
    }
  }

  /**
   * Get consolidated summary of all saved items data in a single API call
   * Replaces 9+ individual API calls for better performance
   */
  static async getSummary(limit = 20, offset = 0) {
    return await SavedItemsServiceDB.getSummary(limit, offset);
  }

}

export default SavedItemsService;